import React, { useState, useMemo } from "react";
import { 
    Box, 
    Paper, 
    Typography, 
    Avatar, 
    IconButton,
    Tooltip,
    Fade
} from "@mui/material";
import { 
    Person as PersonIcon,
    ContentCopy as CopyIcon,
    Check as CheckIcon
} from "@mui/icons-material";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import dayjs from "dayjs";
import ShikaLogo from "./ShikaLogo";

const MessageItem = ({ message, isLastMessage }) => {
    const [copied, setCopied] = useState(false);
    const isBot = message.messageType === "bot";
    const messageText = isBot ? message.response : message.message;

    const handleCopyMessage = async () => {
        try {
            await navigator.clipboard.writeText(messageText);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error("Failed to copy message:", err);
        }
    };

    const formatMessageTime = (timestamp) => {
        return dayjs(timestamp).format("HH:mm");
    };

    // Custom components for markdown rendering - using useMemo to prevent recreation
    const markdownComponents = useMemo(() => ({
        p: ({ children }) => (
            <Box 
                component="p" 
                sx={{ 
                    m: 0, 
                    mb: 1, 
                    lineHeight: 1.6, 
                    overflowWrap: 'anywhere',
                    fontSize: '0.75rem' // 30% smaller font
                }}
            >
                {children}
            </Box>
        ),
        h1: ({ children }) => (
            <Typography 
                variant="h6" 
                component="h1" 
                sx={{ 
                    mt: 1.5, 
                    mb: 1,
                    fontSize: '0.9rem' // smaller heading
                }}
            >
                {children}
            </Typography>
        ),
        h2: ({ children }) => (
            <Typography 
                variant="subtitle1" 
                component="h2" 
                sx={{ 
                    mt: 1.5, 
                    mb: 1,
                    fontSize: '0.85rem' // smaller heading
                }}
            >
                {children}
            </Typography>
        ),
        h3: ({ children }) => (
            <Typography 
                variant="subtitle2" 
                component="h3" 
                sx={{ 
                    mt: 1.2, 
                    mb: 0.8,
                    fontSize: '0.8rem' // smaller heading
                }}
            >
                {children}
            </Typography>
        ),
        ul: ({ children }) => (
            <Box 
                component="ul" 
                sx={{ 
                    my: 1, 
                    pl: 3, 
                    lineHeight: 1.6,
                    fontSize: '0.75rem' // smaller font
                }}
            >
                {children}
            </Box>
        ),
        ol: ({ children }) => (
            <Box 
                component="ol" 
                sx={{ 
                    my: 1, 
                    pl: 3, 
                    lineHeight: 1.6,
                    fontSize: '0.75rem' // smaller font
                }}
            >
                {children}
            </Box>
        ),
        li: ({ children }) => (
            <Box 
                component="li" 
                sx={{ 
                    lineHeight: 1.6,
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                    fontSize: '0.75rem' // smaller font
                }}
            >
                {children}
            </Box>
        ),
        blockquote: ({ children }) => (
            <Box 
                component="blockquote" 
                sx={{ 
                    borderLeft: '3px solid',
                    borderColor: 'primary.main',
                    pl: 2,
                    my: 1,
                    fontStyle: 'italic',
                    backgroundColor: 'action.hover',
                    borderRadius: 1,
                    py: 1,
                    fontSize: '0.75rem' // smaller font
                }}
            >
                {children}
            </Box>
        ),
        code: ({ inline, children }) => inline ? (
            <Box 
                component="code" 
                sx={{
                    px: 0.5, 
                    py: 0.1, 
                    borderRadius: 0.5,
                    bgcolor: 'action.hover',
                    fontFamily: '"Fira Code", "Consolas", "Monaco", monospace', 
                    fontSize: '0.9em'
                }}
            >
                {children}
            </Box>
        ) : (
            <Box 
                component="pre" 
                sx={{
                    my: 1, 
                    p: 1.5, 
                    borderRadius: 1,
                    bgcolor: 'action.selected',
                    fontFamily: '"Fira Code", "Consolas", "Monaco", monospace', 
                    fontSize: '0.85em',
                    lineHeight: 1.6,
                    // Critical fixes:
                    whiteSpace: 'pre',          // no text wrapping in code
                    overflowX: 'auto',          // horizontal scroll only
                    maxWidth: '100%'
                }}
            >
                {children}
            </Box>
        ),
        strong: ({ children }) => (
            <Box component="strong" sx={{ fontWeight: 'bold' }}>
                {children}
            </Box>
        ),
        em: ({ children }) => (
            <Box component="em" sx={{ fontStyle: 'italic' }}>
                {children}
            </Box>
        ),
        a: ({ children, href }) => (
            <Box 
                component="a" 
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                    color: 'primary.main',
                    textDecoration: 'underline',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                    fontSize: '0.75rem' // smaller font
                }}
            >
                {children}
            </Box>
        )
    }), []);

    return (
        <Fade in={true} timeout={300}>
            <Box sx={{ 
                display: "flex", 
                justifyContent: isBot ? "flex-start" : "flex-end",
                mb: 1,
                alignItems: "flex-start"
            }}>
                {isBot && (
                    <Avatar 
                        sx={{ 
                            width: 38, 
                            height: 38, 
                            mr: 1,
                            bgcolor: "transparent",
                            p: 0.5
                        }}
                    >
                        <ShikaLogo size={30} />
                    </Avatar>
                )}

                <Box sx={{ 
                    maxWidth: "70%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: isBot ? "flex-start" : "flex-end"
                }}>
                    <Paper 
                        elevation={1}
                        sx={{ 
                            p: 1.5,
                            backgroundColor: isBot ? "background.paper" : "primary.main",
                            color: isBot ? "text.primary" : "primary.contrastText",
                            borderRadius: 2,
                            borderTopLeftRadius: isBot ? 0.5 : 2,
                            borderTopRightRadius: isBot ? 2 : 0.5,
                            position: "relative",
                            "&:hover .message-actions": {
                                opacity: 1
                            }
                        }}
                    >
                        {isBot ? (
                            <Box sx={{ 
                                lineHeight: 1.6
                                // Removed conflicting first-child/last-child rules
                            }}>
                                <ReactMarkdown 
                                    remarkPlugins={[remarkGfm]}
                                    components={markdownComponents}
                                >
                                    {messageText}
                                </ReactMarkdown>
                            </Box>
                        ) : (
                            <Typography 
                                variant="body1" 
                                sx={{ 
                                    lineHeight: 1.4,
                                    wordBreak: "break-word",
                                    margin: 0,
                                    fontSize: '0.75rem' // 30% smaller font
                                }}
                            >
                                {messageText}
                            </Typography>
                        )}

                        {/* Message Actions */}
                        <Box 
                            className="message-actions"
                            sx={{ 
                                position: "absolute",
                                top: -8,
                                right: isBot ? -8 : "auto",
                                left: isBot ? "auto" : -8,
                                opacity: 0,
                                transition: "opacity 0.2s",
                                zIndex: 1
                            }}
                        >
                            <Tooltip title={copied ? "Copied!" : "Copy message"}>
                                <IconButton 
                                    size="small"
                                    onClick={handleCopyMessage}
                                    sx={{ 
                                        bgcolor: "background.paper",
                                        boxShadow: 1,
                                        "&:hover": {
                                            bgcolor: "background.paper"
                                        }
                                    }}
                                >
                                    {copied ? <CheckIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Paper>

                    {/* Timestamp */}
                    <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ 
                            mt: 0.5,
                            fontSize: "0.7rem"
                        }}
                    >
                        {formatMessageTime(message.timestamp)}
                    </Typography>
                </Box>

                {!isBot && (
                    <Avatar 
                        sx={{ 
                            width: 32, 
                            height: 32, 
                            ml: 1,
                            bgcolor: "secondary.main"
                        }}
                    >
                        <PersonIcon fontSize="small" />
                    </Avatar>
                )}
            </Box>
        </Fade>
    );
};

export default MessageItem; 